import type { MemberListInput, MemberRepository, TenantContext } from '@zetyar/data-contracts'
import type { Membership, RoleName } from '@zetyar/domain'
import { isKnownRole } from '@zetyar/domain'
import type { SupabaseClient } from '@supabase/supabase-js'

interface MembershipRow {
  readonly id: string
  readonly organization_id: string
  readonly person_id: string
  readonly display_name: string
  readonly position: string | null
  readonly department: string | null
  readonly institutional_email: string | null
  readonly member_number: string | null
  readonly role: string
  readonly status: string
  readonly joined_at: string | null
  readonly created_at: string
  readonly updated_at: string
  readonly membership_roles: readonly { readonly role: string }[]
}

function mapMembership(row: MembershipRow): Membership {
  const roles: RoleName[] = row.membership_roles.map((r) => r.role).filter(isKnownRole)
  if (roles.length === 0 && isKnownRole(row.role)) roles.push(row.role)

  return {
    id: row.id,
    organizationId: row.organization_id,
    userId: row.person_id,
    roles,
    status: row.status as Membership['status'],
    joinedAt: row.joined_at ?? row.created_at,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    displayName: row.display_name,
    institutionalEmail: row.institutional_email,
    position: row.position,
    department: row.department,
    memberNumber: row.member_number,
  }
}

export function createSupabaseMemberRepository(client: SupabaseClient): MemberRepository {
  return {
    async list(context: TenantContext, { limit }: MemberListInput) {
      const { data, error } = await client
        .from('memberships')
        .select('id, organization_id, person_id, display_name, position, department, institutional_email, member_number, role, status, joined_at, created_at, updated_at, membership_roles (role)')
        .eq('organization_id', context.organizationId)
        .order('display_name', { ascending: true })
        .limit(Math.max(1, Math.min(limit, 200)))

      if (error) throw error
      return (data as unknown as MembershipRow[]).map(mapMembership)
    },

    async updateStatus(context: TenantContext, membershipId, status) {
      const { error } = await client.rpc('update_membership_status', {
        p_id: membershipId,
        p_organization_id: context.organizationId,
        p_status: status,
      })
      if (error) throw error
    },

    // Always through the RPC: it enforces organization_admin and writes audit_logs/notifications.
    // An empty string clears position/department/institutional email (0032); undefined keeps them.
    async updateDetails(context: TenantContext, membershipId, details) {
      const { error } = await client.rpc('update_membership_details', {
        p_id: membershipId,
        p_organization_id: context.organizationId,
        p_display_name: details.displayName ?? null,
        p_position: details.position ?? null,
        p_department: details.department ?? null,
        p_institutional_email: details.institutionalEmail ?? null,
        p_role: details.role ?? null,
      })
      if (error) throw error
    },

    async assignRole(context: TenantContext, membershipId, role) {
      const { error } = await client.rpc('assign_membership_role', {
        p_organization_id: context.organizationId,
        p_membership_id: membershipId,
        p_role: role,
      })
      if (error) throw error
    },

    async revokeRole(context: TenantContext, membershipId, membershipRoleId, role) {
      const { error } = await client.rpc('revoke_membership_role', {
        p_organization_id: context.organizationId,
        p_membership_id: membershipId,
        p_membership_role_id: membershipRoleId,
        p_role: role,
      })
      if (error) throw error
    },
  }
}
